import type { DrinkSize, FastfoodItem, Per100g } from '@gramas/shared'
import { dedupeFoods, rankFoods } from '@gramas/shared'
import { type LocalFood, searchKeyFor } from '../db/dexie.js'
import { cacheFood, foodUsage, searchFoodsLocal } from '../db/repo.js'
import { api } from './client.js'

interface RemoteFood {
  id: string
  nameLt: string | null
  nameEn: string | null
  brand: string | null
  kcal: number
  protein: number
  carbs: number
  fat: number
  sugars: number | null
  satFat: number | null
  fiber: number | null
  salt: number | null
  servingSizeG: number | null
  servingName: string | null
  /** Optional: an instance deployed before pieces shipped omits it. */
  pieceWeightG?: number | null
  /** Optional: an instance deployed before units shipped omits it. */
  unit?: 'g' | 'ml'
  isVerified: boolean
  visibility: 'public' | 'private'
  updatedAt: string
  deletedAt: string | null
}

/**
 * A server row as it would look on the phone, without being written there.
 * Storing it is what `cacheFood` does, and that only happens once the user
 * picks the result — the catalog is far too large to keep on spec.
 */
function toLocal(food: RemoteFood): LocalFood {
  return {
    ...food,
    // Older instances predate the column; grams is what they meant.
    unit: food.unit ?? 'g',
    searchKey: searchKeyFor(food.brand, food.nameLt, food.nameEn),
    cachedAt: null,
    pinned: 0,
    dirty: 0,
  }
}

/**
 * Search the catalog, server first.
 *
 * The server holds ten thousand Open Food Facts products and tens of thousands
 * of scraped ones, which is exactly what the phone is no longer trying to keep,
 * so a search that only ever read the local slice would find a fraction of the
 * shop. When the request cannot be made — offline on a plane, or the VPS is
 * down — the local slice answers instead: it still holds the diary's foods, the
 * recipes' ingredients and everything ever scanned, which is most of what
 * anyone searches for twice.
 */
export async function searchFoodsRemote(query: string, limit = 25): Promise<LocalFood[]> {
  if (!navigator.onLine) return searchFoodsLocal(query, limit)
  try {
    const res = await api.get<{ query: string; results: Array<{ food: RemoteFood }> }>(
      `/api/foods/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    )
    return res.results.map((match) => toLocal(match.food))
  } catch {
    return searchFoodsLocal(query, limit)
  }
}

/**
 * One list, from both halves of the catalogue.
 *
 * The phone and the server hold different slices — a few thousand rows that
 * have been eaten before, and twelve thousand that mostly have not — and both
 * are searched because either can hold the answer. What they must not do is
 * take turns: every screen used to render the local hits first and append the
 * server's behind them, so a phone that had cached forty products containing
 * "pomidor" pushed the server's best answer to somewhere past position forty,
 * in alphabetical order. Origin is not relevance.
 *
 * `onLocal` receives the phone's answer the moment it exists, so the list is
 * never empty while the request is in flight; the promise resolves with both
 * ranked together.
 */
export async function searchFoodsEverywhere(
  query: string,
  limit = 30,
  onLocal?: (foods: LocalFood[]) => void,
): Promise<LocalFood[]> {
  // A wider local pool than is shown: these rows are free to fetch and the
  // ranking decides which survive the merge.
  const local = await searchFoodsLocal(query, Math.max(limit, 40))
  onLocal?.(local.slice(0, limit))

  const remote = await searchFoodsRemote(query, limit).catch(() => [])
  const seen = new Set(local.map((food) => food.id))
  const pool = [...local, ...remote.filter((food) => !seen.has(food.id))]
  const usage = await foodUsage()
  const ranked = rankFoods(query, pool, { usage }).map((match) => match.food)
  return dedupeFoods(ranked).slice(0, limit)
}

/**
 * One catalogue row by id, cached on the way past.
 *
 * A recipe synced from another device names foods this phone has never held —
 * the recipe row travels, the catalogue rows do not — and an ingredient list
 * that silently drops those would delete them the next time the recipe is
 * saved. Returns null when the row is genuinely gone, so the caller can say so
 * rather than pretend the ingredient was never there.
 */
export async function fetchFoodRemote(id: string): Promise<LocalFood | null> {
  try {
    const { food } = await api.get<{ food: RemoteFood }>(`/api/foods/${encodeURIComponent(id)}`)
    return await cacheFood(toLocal(food))
  } catch {
    return null
  }
}

/**
 * The groups worth browsing, which is a much shorter list than the groups the
 * catalogue keeps.
 *
 * Browsing earns its place only where typing does not work. An apple has no
 * barcode, no brand and no spelling the user can be sure the catalogue agrees
 * with, so a grid of six tiles beats a search box. Flour, vinegar and yoghurt
 * have none of those problems: they are reached by name, by barcode, or as a
 * line inside a recipe, and putting them on the shelf here only made the shelf
 * longer to read.
 *
 * Meat and fish stay despite being cooked rather than eaten raw, for the apple's
 * reason rather than the apple's: they are bought loose at a counter, carry no
 * barcode either, and the catalogue's name for a cut is exactly what nobody can
 * guess.
 */
export const FOOD_CATEGORIES = [
  { key: 'vaisiai', label: 'Vaisiai' },
  { key: 'darzoves', label: 'Daržovės' },
  { key: 'uogos', label: 'Uogos' },
  { key: 'riesutai', label: 'Riešutai' },
  { key: 'mesa', label: 'Mėsa' },
  { key: 'zuvis', label: 'Žuvis' },
] as const

export async function browseFoods(category: string): Promise<LocalFood[]> {
  const res = await api.get<{ results: RemoteFood[] }>(
    `/api/foods/browse?category=${encodeURIComponent(category)}`,
  )
  return res.results.map(toLocal)
}

export interface IdentifiedFood {
  name: string
  nameEn: string | null
  /** Pieces of it in the photograph: three apples is 3. Never below 1. */
  count: number
  /** Grams of ONE piece, so a count of three multiplies rather than divides. */
  portionG: number | null
  confidence: number
  candidates: LocalFood[]
}

/**
 * Ask the server what is in a photograph. Produce carries no barcode and no
 * label, so this is the only automatic way in; the numbers still come from a
 * catalogue row the user picks, never from the model.
 */
export async function identifyPhoto(image: Blob): Promise<IdentifiedFood[]> {
  const form = new FormData()
  form.append('image', image, 'photo.jpg')
  const res = await api.post<{
    items: Array<{
      name: string
      nameEn: string | null
      count?: number
      portionG: number | null
      confidence: number
      candidates: Array<{ food: RemoteFood }>
    }>
  }>('/api/ai/identify', form)

  return res.items.map((item) => ({
    name: item.name,
    nameEn: item.nameEn,
    count: Math.max(1, Math.round(item.count ?? 1)),
    portionG: item.portionG,
    confidence: item.confidence,
    candidates: item.candidates.map((c) => toLocal(c.food)),
  }))
}

export interface BarcodeResolution {
  tier: 'local' | 'off' | 'usda' | 'none'
  food: LocalFood | null
  code: string
}

/**
 * Ask the server to resolve a barcode through its lookup tiers. A hit is cached
 * locally — a scan is a use, and the next scan of the same product needs no
 * network at all.
 */
export async function resolveBarcodeRemote(code: string): Promise<BarcodeResolution> {
  const res = await api.get<{
    tier: BarcodeResolution['tier']
    food: RemoteFood | null
    code: string
  }>(`/api/foods/by-barcode/${encodeURIComponent(code)}`)
  if (!res.food) return { tier: res.tier, food: null, code: res.code }
  return { tier: res.tier, food: await cacheFood(toLocal(res.food)), code: res.code }
}

export interface CreateFoodPayload {
  id: string
  nameLt: string | null
  nameEn: string | null
  brand: string | null
  servingSizeG: number | null
  nutrients: Per100g
  sourceKind: 'user_ocr' | 'user_manual'
  barcode: { code: string; symbology: string } | null
}

export async function createFoodRemote(payload: CreateFoodPayload): Promise<void> {
  await api.post('/api/foods', payload)
}

/**
 * Counter items matching a search, so one search covers the shop and the chains.
 *
 * Kept separate from the food search rather than merged server-side: these are
 * assemblies rather than products — a latte is a cup, a milk and whatever you
 * added — and flattening them into the catalogue would either lose that or fill
 * `foods` with rows nobody can edit.
 */
export async function searchFastfoodItems(query: string, limit = 8): Promise<FastfoodItem[]> {
  if (query.trim().length < 2) return []
  const { items } = await api.get<{ items: FastfoodItem[] }>(
    `/api/fastfood/items?q=${encodeURIComponent(query.trim())}&limit=${limit}`,
  )
  return items
}

export interface FastfoodChainSummary {
  id: number
  slug: string
  name: string
  drinkSizes: DrinkSize[] | null
}

export async function fastfoodChains(): Promise<FastfoodChainSummary[]> {
  const { chains } = await api.get<{ chains: FastfoodChainSummary[] }>('/api/fastfood/chains')
  return chains
}
